import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'

import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import TextButton from 'components/Luxkit/Button/TextButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import cn from 'clsx'

import Tooltip from 'components/Luxkit/Tooltip'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Pill from 'components/Luxkit/Pill'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import LineClockDollarIcon from 'components/Luxkit/Icons/line/LineClockDollarIcon'
import CruiseDiscountTermsAndConditions from 'components/Cruises/CruiseDiscountTermsAndConditions'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import { connect } from 'react-redux'
import Label from 'components/Luxkit/Label/Label'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import useQueryParams from 'hooks/useQueryParams'
import { CABIN_CATEGORIES } from 'constants/cruise'
import PrimaryButtonPaletteProvider, { PrimaryButtonPalette } from 'contexts/PrimaryButtonPaletteProvider'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import { useIsTabletAndSmallerScreen } from 'lib/web/deviceUtils'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import FloatingBookmarkContainer from 'tripPlanner/components/Bookmark/Common/FloatingBookmarkContainer'
import { getCruiseOfferPriceVariation } from 'lib/cruises/cruiseUtils'
import PromotionInclusionList from 'components/Cruises/PromotionInclusions/PromotionInclusionList'
import TextLink from 'components/Luxkit/TextLink'

const BadgeContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const Cruise = styled.div`
  grid-template-columns: 1fr;
  grid-template-rows: ${rem(220)} 1fr;
  position: relative;

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: ${rem(150)} 1fr;
    grid-template-rows: 1fr;
  }
  ${mediaQueryUp.desktop} {
    display: grid;
    grid-template-columns: ${rem(280)} 1fr;
    grid-template-rows: 1fr;
  }
`
const CruiseContainer = styled.div`
 border-radius: ${props => props.theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  background-color: ${(props) => props.theme.palette.neutral.eight};
`

const TileMediaContainer = styled.div`
  display: flex;
  position: relative;
  height: ${rem(186)};
  min-width: 280px;
  max-width: 700px;

  ${mediaQueryUp.tablet} {
    min-width: 150px;
    max-width: 150px;
    height: 100%;
  }
  ${mediaQueryUp.desktop} {
    max-width: 280px;
    height: 100%;
  }
`

const DetailsCaption = styled(BodyText)`
  align-items: center;
  display: flex;
  overflow: visible;
  flex-flow: wrap;

  > div::after {
    color: ${({ theme }) => theme.palette.neutral.one};
    content: "·";
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
  > div:last-child::after{
    content: "";
  }


`

const OperatorLogoImage = styled(ResponsiveImage)`
  width: 100%;
  height: 100%;
`

const LogoContainer = styled.div`
  width: 100%;
  display: flex;
  gap: ${rem(4)};
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${rem(8)};
  ${mediaQueryUp.desktop} {
    gap: ${rem(32)};
  }

  &:empty {
    display: none;
  }
`
const PriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${mediaQueryUp.desktop} {
    gap: ${rem(16)};
  }
  ${mediaQueryUp.tablet} {
    display: block;
    flex-direction: none;
  }
`
const FooterCaption = styled.div`
  font-size: smaller;
  color: ${props => props.theme.palette.neutral.three};
`
const DetailsItem = styled.div`
  min-width: ${rem(60)};
`

const PriceSection = styled.div`
  display: flex;
  justify-content: end;
  flex: 1;
  align-items: flex-end;
  width: 100%;
  flex-direction: column;
  gap: ${rem(16)};


  ${mediaQueryUp.tablet} {
    max-width: ${rem(200)};
    min-width: ${rem(200)};
    min-height: ${rem(270)};
    display: flex;
    flex-direction: column;
    gap: ${rem(64)};
  }
`

const OperatorLogoArea = styled.div`
    display: none;
    max-width: ${rem(150)};
    align-self: center;

  ${mediaQueryUp.desktop} {
    display: flex;
    justify-content: center;
  }
`

const PriceSummary = styled.div`
  position: relative;
  width: 100%;
`

const PriceSectionButton = styled(TextButton)`
  position: absolute;
  bottom: 0;
  right: 0;
  margin-top: ${rem(12)};
  position: static;
  width: auto;

  ${mediaQueryUp.tablet} {
    width: 100%;
  }
`

const Description = styled.div`
  position: relative;
  padding: ${rem(20)};
  gap: ${rem(12)};
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)};
    gap: ${rem(10)};
    grid-template-columns: 1fr ${rem(162)};
  }
`

const DescriptionBody = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: space-between;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    flex-direction: column;
    gap: ${rem(16)};
  }
  ${mediaQueryUp.desktop} {
    flex-direction: row;
    gap: ${rem(8)};
  }
`

const OperatorLogoMobile = styled.div`
  display: flex;
  min-width: ${rem(78)};
  max-width: ${rem(90)};
  max-height: ${rem(60)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const DescriptionSection = styled(VerticalSpacer)`
  height: 100%;
  display: block;
  max-width: 100%;
  display: flex;
  flex: 3;

  ${mediaQueryUp.tablet} {
    flex: 3;
  }
  ${mediaQueryUp.desktop} {
    max-width: ${rem(300)};
    flex: 1.7;
  }
`
const DescriptionSpace = styled(VerticalSpacer)`
  width: 100%;
`

const Separator = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.palette.neutral.five};
  background-color: ${(props) => props.theme.palette.neutral.eight};
  max-width: 90%;

  ${mediaQueryUp.desktop} {
    max-width: 100%;
    max-height: ${rem(1)};
  }
`

const Prices = styled.div`
  padding:${rem(20)};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${rem(4)};
  .priceSize{
    font-size: 10px;
  }

  ${mediaQueryUp.desktop} {
    padding:${rem(16)};
    grid-area: prices;
    display: flex;
  }
`
const TaxesAndFees = styled.div`
  min-width: ${rem(200)};
  align-self: center;
`

const CabinPrice = styled.div`
  width: 100%;

  &.defocus {
    opacity: 0.5;
  }
`
const ButtonContainer = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(20)} ${rem(20)};
    grid-template-columns: 2fr 1fr;
    align-items: center;
    display: grid;
    grid-area: cta;
    display: flex;
    justify-content: center;
    flex-direction: column-reverse;
    padding: 0 0 0 ${rem(20)};
  }
`

const ButtonContainerMobile = styled(ButtonContainer)`
  display: flex;
  justify-content: flex-end;
  grid-column: 2;
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const Discount = styled.div`
  padding: ${rem(20)} ${rem(20)} 0;
`

const MEDIA_IMAGE_PARAMS: ImageParams = {
  quality: 'eco',
  mobileAspectRatio: '16:9',
  tabletAspectRatio: '16:9',
  desktopAspectRatio: '16:11',
  largeDesktopAspectRatio: '16:11',
  mobileWidth: '100vw',
  tabletWidth: '100vw',
  desktopWidth: '780px',
  largeDesktopWidth: '780px',
}

interface MappedProps {
  isVelocityEnabled: boolean;
}

const DestinationDescriptionText = styled(MarkdownRender)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
    margin-top: ${rem(12)};
  }
`
interface Props {
  flashOffer?: boolean;
  productType: App.ProductType;
  images: App.Image[];
  name: string;
  cruiseLine: App.CruiseLine;
  shipName?: string;
  duration?: number;
  disableDeposits?: boolean;
  departurePortCity?: string;
  returnPortCity?: string;
  itinerary?: App.CruiseItineraryItem[];
  promotionDetails?: App.CruisePromotionDetails | null;
  hasTheSameStartAndEndPort?: boolean;
  cities?: (string | undefined)[];
  placesCount?: number;
  countriesCount?: number;
  earliestDepartureDate?: string;
  laterDates?: string[];
  lowestOverallPriceDetails?: App.CruisePriceDetails;
  lowestPriceDetailsByCategory?: App.LowestPriceDetailsByCategory;
  primaryPrice?:{
    total: number;
    type: string;
  },
  secondaryPrice?:{
    total: number;
    type: string;
  }
  discountPills?: App.CruiseDiscountPillDetails | null
  pricingSection?: React.ReactNode;
  inclusions?: string[];
  bookmarkButton: React.ReactNode;
  offer?: App.CruiseOffer;
  paletteValue: PrimaryButtonPalette;
  parsedDescription?: string;
  evergreenInclusions?: App.EvergreenInclusion[];
}
const formatCruisLineName = (name: string) => {
  if (name) return name.replace('Cruises', '').replace('International', '')
  return
}

function CruisesOfferTile(props: Props & MappedProps) {
  const {
    flashOffer,
    isVelocityEnabled,
    productType,
    images,
    cruiseLine,
    duration,
    name,
    shipName,
    disableDeposits,
    promotionDetails,
    hasTheSameStartAndEndPort,
    departurePortCity,
    returnPortCity,
    cities,
    placesCount,
    countriesCount,
    earliestDepartureDate,
    laterDates,
    discountPills,
    pricingSection,
    bookmarkButton,
    lowestPriceDetailsByCategory,
    paletteValue,
    parsedDescription,
    offer,
    evergreenInclusions,
  } = props

  const isMobileScreen = useIsTabletAndSmallerScreen()
  const queryParams = useQueryParams()
  const pricePerCategory = useMemo(() => {
    if (lowestPriceDetailsByCategory) {
      return Object.entries(lowestPriceDetailsByCategory).map(([category, price]) => {
        const categoryIncluded = CABIN_CATEGORIES.some((item) => item.category === category)
        if (categoryIncluded) return price
      }).filter(Boolean).sort((a, b) => (a?.total ?? 0) - (b?.total ?? 0)) }
  }, [lowestPriceDetailsByCategory])
  const cabinTypes = queryParams.getAll('cabinTypes')
  const minPrice = parseFloat(queryParams.get('price.min') || '0')
  const maxPrice = parseFloat(queryParams.get('price.max') || '0')
  const cruiseLineName = formatCruisLineName(cruiseLine?.name)
  const commaSeparatedCities = useMemo(() => {
    if (cities) {
      return [...new Set(cities)].join(' - ')
    }
  }, [cities])

  return (
    <CruiseContainer>
      <PrimaryButtonPaletteProvider palette={paletteValue}>
        <Cruise>
          <TileMediaContainer>
            <ImageCarousel
        topLeft={
          <BadgeContainer>
            <ProductTypeLabel productType={productType} />
            {isVelocityEnabled && <PartnershipLabel canEarn canUse variant="icon" />}
          </BadgeContainer>}
          images={images}
          imageParams={MEDIA_IMAGE_PARAMS}
          eagerLoadFirstImage
          />
          </TileMediaContainer>
          <Description>
            <DescriptionBody>
              <DescriptionSection gap={8}>
                <DescriptionSpace gap={0}>
                  <LabelGroup>
                    {promotionDetails?.isNew && <Label kind="tertiary" variant="ghost">NEW</Label>}
                    {!!promotionDetails?.isEndingIn && promotionDetails.isEndingIn <= 7 && (
                      <Label kind="primary" variant="urgency">
                        OFFER ENDS IN {pluralizeToString('day', promotionDetails.isEndingIn).toLocaleUpperCase()}
                      </Label>
                    )}
                  </LabelGroup>
                  <LogoContainer>
                    <DescriptionSpace gap={4}>
                      <Caption variant="large">
                        {hasTheSameStartAndEndPort && returnPortCity && (
                          <span>
                            Starts and ends in <b>{returnPortCity}</b>
                          </span>
                        )}
                        {!hasTheSameStartAndEndPort && returnPortCity && (
                          <span>
                            Starts in <b>{departurePortCity}</b>. Ends in <b>{returnPortCity}</b>
                          </span>
                        )}
                      </Caption>

                      <Heading variant="heading5" lineClamp={3}>
                        {name}
                      </Heading>
                    </DescriptionSpace>
                    {cruiseLine.imageId && (
                      <OperatorLogoMobile>
                        <OperatorLogoImage
                      id={cruiseLine.imageId}
                      mobileWidth="75px"
                      tabletWidth="150px"
                      quality="eco"
                      alt={cruiseLine.name}
                      role="presentation"
                    />
                      </OperatorLogoMobile>
                    )}
                  </LogoContainer>
                  <DetailsCaption variant="medium">
                    {cruiseLineName && <div>
                      {cruiseLineName}
                    </div>}
                    {shipName && <div>
                      {shipName}
                    </div>}
                  </DetailsCaption>

                  <DetailsCaption variant="medium">
                    {duration && <DetailsItem>{pluralizeToString('night', duration)}</DetailsItem>}
                    {!!placesCount && <DetailsItem>{pluralizeToString('port', placesCount)}</DetailsItem>}
                    {!!countriesCount && <DetailsItem>{pluralizeToString('country', countriesCount)}</DetailsItem>}
                    {commaSeparatedCities && <Tooltip description={commaSeparatedCities}>
                      <CSSBreakpoint min="tablet">View itinerary</CSSBreakpoint>
                      <CSSBreakpoint max="mobile"><SolidInfoCircleIcon size="XS" /></CSSBreakpoint>
                    </Tooltip>}
                  </DetailsCaption>

                  <VerticalSpacer gap={12}>
                    {parsedDescription && !promotionDetails && (
                      <DestinationDescriptionText
                        type="compact"
                        fontSize="medium"
                        fontWeight="normal"
                        content={parsedDescription}
                      />
                    )}
                    <PromotionInclusionList
                      promotionDetails={promotionDetails}
                      evergreenInclusions={evergreenInclusions}
                    />
                    {earliestDepartureDate && <BodyText variant="medium" weight="bold" >
                      Departing on {earliestDepartureDate}
                      {!!laterDates?.length && <>
                        {' '}
                        <Tooltip
                          placement="top"
                          disabled={flashOffer}
                          description={laterDates.join(', ')}
                        >
                          <TextLink>and more</TextLink>
                        </Tooltip>
                      </>}
                    </BodyText>}
                  </VerticalSpacer>

                </DescriptionSpace>
              </DescriptionSection>

              <PriceSection>
                <TripGuard>
                  <CSSBreakpoint max="tablet">
                    <OffsetBookmarkContainer>
                      {bookmarkButton}
                    </OffsetBookmarkContainer>
                  </CSSBreakpoint>
                  <CSSBreakpoint min="desktop">
                    <FloatingBookmarkContainer>
                      {bookmarkButton}
                    </FloatingBookmarkContainer>
                  </CSSBreakpoint>
                </TripGuard>
                {cruiseLine.imageId && (
                  <OperatorLogoArea>
                    <OperatorLogoImage
                  id={cruiseLine.imageId}
                  mobileWidth="75px"
                  tabletWidth="150px"
                  quality="eco"
                  alt={cruiseLine.name}
                  role="presentation"
                />
                  </OperatorLogoArea>
                )}
                {!pricingSection && !isMobileScreen && <PriceSummary>
                  <VerticalSpacer gap={8}>
                    {!disableDeposits && (
                      <div>
                        <BodyText variant="small">
                          {promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                            Secure with a {promotionDetails.deposit.formattedValue} deposit
                          </Pill>}
                          {!promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                            Deposits available
                          </Pill>}
                        </BodyText>
                      </div>
                    )}

                    <PriceContainer>
                      {(!isMobileScreen) && <PriceSectionButton kind="primary" nonInteractable >
                        View offer
                      </PriceSectionButton>}
                    </PriceContainer>

                  </VerticalSpacer>
                </PriceSummary>}
                {pricingSection}
              </PriceSection>
            </DescriptionBody>
          </Description>

        </Cruise>
        {pricePerCategory &&
          <>
            <Separator />
            {isMobileScreen && !disableDeposits && (
              <Discount>
                <BodyText variant="small">
                  {promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                    Secure with a {promotionDetails.deposit.formattedValue} deposit
                  </Pill>}
                  {!promotionDetails?.deposit && <Pill kind="tertiary" variant="primary" startIcon={<LineClockDollarIcon />}>
                    Deposits available
                  </Pill>}
                </BodyText>
              </Discount>
            )}
            <Prices>
              {CABIN_CATEGORIES.map((cabinCategory) => {
                const price = pricePerCategory?.find((price) => price?.cabinCategory === cabinCategory.category)
                if (price && offer) {
                  const { primaryPrice, secondaryPrice } = getCruiseOfferPriceVariation(offer, price)
                  const isHighlightedPrice = (
                    (!minPrice || price.total >= minPrice) &&
                  (!maxPrice || price.total <= maxPrice) &&
                  (!cabinTypes?.length || cabinTypes.includes(price.cabinCategory))
                  )
                  if (primaryPrice?.type === 'night') {
                    return (<CabinPrice key={price.cabinCategory} className={cn({ defocus: !isHighlightedPrice })}>
                      <BodyText variant="small" colour="neutral-three">{price.cabinCategory} from</BodyText>
                      <Heading variant="heading6" as="span">
                        <FormatCurrency value={secondaryPrice?.total ?? 0} />
                      </Heading>
                      <BodyText variant="small" weight="bold" as="span">
                        /{secondaryPrice?.type}
                      </BodyText>
                    </CabinPrice>)
                  }

                  return (
                    <CabinPrice key={price.cabinCategory} className={cn({ defocus: !isHighlightedPrice })}>
                      <BodyText variant="small" colour="neutral-three">{price.cabinCategory} from</BodyText>
                      <Heading variant="heading6" as="span">
                        <FormatCurrency value={primaryPrice?.total ?? 0} />
                      </Heading>
                      <BodyText variant="small" weight="bold" as="span">
                        /{primaryPrice?.type}
                      </BodyText>
                    </CabinPrice>
                  )
                }
                return (
                  <CabinPrice key={cabinCategory.category}>
                    <BodyText variant="small" colour="neutral-three">{cabinCategory.category}</BodyText>
                    <Heading variant="heading5" colour="neutral-three" as="span">
                      Unavailable
                    </Heading>
                  </CabinPrice>
                )
              })}

              <TaxesAndFees>
                <ValueWrapper variant="large" weight="bold">
                  {discountPills?.discountPercentage &&
                    <>
                      <Pill kind="primary" variant="success">
                        Save up to {discountPills.discountPercentage}%
                      </Pill>
                      <CruiseDiscountTermsAndConditions />
                    </>
                  }
                  <FooterCaption>
                    All prices include taxes & fees
                  </FooterCaption>
                </ValueWrapper>
              </TaxesAndFees>
              <ButtonContainerMobile>
                {isMobileScreen && <TextButton kind="primary" fit="auto">View</TextButton>}
                {!isMobileScreen && <TextButton kind="primary" fit="flex">View offer</TextButton>}
              </ButtonContainerMobile>
            </Prices>
          </>
      }
      </PrimaryButtonPaletteProvider>
    </CruiseContainer>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  isVelocityEnabled: state.geo.partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE,
})

export default connect(mapStateToProps)(CruisesOfferTile)
